import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success("Welcome back!");
      nav(next || (u.role === "admin" ? "/admin" : "/app"), { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScaffold title="Welcome back" subtitle="Log in to manage your tags.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" value={email} onChange={setEmail} type="email" testid="login-email" placeholder="you@example.com" />
        <Field label="Password" value={password} onChange={setPassword} type="password" testid="login-password" placeholder="••••••••" />
        <button data-testid="login-submit" disabled={busy} className="tap w-full h-14 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Log in <Icon name="ArrowRight" className="w-4 h-4" /></>}
        </button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        New here? <Link to={`/register${next ? `?next=${next}` : ""}`} className="font-semibold text-foreground underline">Create an account</Link>
      </p>
    </AuthScaffold>
  );
}

export function AuthScaffold({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="max-w-md w-full mx-auto px-6 py-8 flex-1 flex flex-col">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight mb-10">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
            <Icon name="ScanLine" className="w-5 h-5" />
          </span>
          Scan2Return
        </Link>
        <div className="animate-rise">
          <h1 className="font-display font-black text-3xl tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-14 px-4 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
        required
      />
    </label>
  );
}
